import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import capitalize from 'lodash/capitalize';
import startCase from 'lodash/startCase';
import type { CSSProperties, ReactNode } from 'react';
import { isValidElement } from 'react';
import { Divider, Segment } from 'semantic-ui-react';

import s from './key-value-list.scss';

export type KeyValueItem = {
  key: string;
  displayName?: ReactNode;
  value: string | number | KeyValueItem[] | object | undefined;
  inline?: boolean;
  show?: boolean;
  forceWrap?: boolean;
  noWrapLabel?: boolean;
  noWrapValue?: boolean;
};

export interface KeyValueListProps {
  className?: string;
  items: KeyValueItem[];
  /**
   * Evenly justifies all row contents against the left and right edges
   */
  justify?: boolean;
  fullWidthValues?: boolean;
  rightAlignLabels?: boolean;
  labelWidthRem?: number;
  labelVerticalAlign?: 'top' | 'center';
  valueColor?: string;
  /**
   * Optionally capitalize string values
   */
  capitalizeValue?: boolean;
}

const KeyValueList = ({
  className,
  items,
  justify = false,
  fullWidthValues = false,
  rightAlignLabels = false,
  labelWidthRem = 15,
  labelVerticalAlign = 'center',
  valueColor,
  capitalizeValue = false,
}: KeyValueListProps) => {
  useStyles(s);
  const formatValue = (
    value: Exclude<KeyValueItem['value'], KeyValueItem[]>,
    item: KeyValueItem,
  ) => {
    if (isValidElement(value)) {
      return (
        <span
          className={classNames(
            s.fieldValue,
            fullWidthValues ? s.fullWidth : '',
            item.noWrapValue ? s.noWrapValue : '',
          )}
          style={{ color: valueColor }}
        >
          {value}
        </span>
      );
    }
    if (typeof value === 'object') {
      return (
        <Segment className={s.objectValue}>
          <pre className={s.jsonStrFmt}>{JSON.stringify(value, null, 2)}</pre>
        </Segment>
      );
    }
    return (
      <span
        className={classNames(
          s.fieldValue,
          fullWidthValues ? s.fullWidth : '',
          item.forceWrap ? s.forceWrap : '',
          item.noWrapValue ? s.noWrapValue : '',
        )}
        style={{ color: valueColor }}
      >
        {typeof value === 'number'
          ? value.toString()
          : (capitalizeValue && capitalize(value)) || value}
      </span>
    );
  };

  const displayItem = (item: KeyValueItem) => {
    if (Array.isArray(item.value)) {
      return (
        <div key={item.key} className={s.fieldRow}>
          <Divider
            className={s.sectionTitle}
            horizontal
            section
            content={<span>{item.displayName || startCase(item.key)}</span>}
          />
          <div>
            {(item.value as KeyValueItem[]).map(subItem =>
              displayItem(subItem),
            )}
          </div>
        </div>
      );
    }

    const isInline =
      (item.inline ?? true) &&
      (typeof item.value !== 'object' || isValidElement(item.value));

    return (
      <div
        key={item.key}
        className={isInline ? s.fieldRowInline : s.fieldRow}
        style={
          isInline
            ? ({ '--kvl-label-width': `${labelWidthRem}rem` } as CSSProperties)
            : undefined
        }
      >
        <span
          className={classNames({
            [s.fieldLabel]: true,
            [s.rightAlign]: rightAlignLabels,
            [s.topAlign]: labelVerticalAlign === 'top',
            [s.noWrapLabel]: item.noWrapLabel,
          })}
        >
          {item.displayName || startCase(item.key)}
        </span>
        {formatValue(item.value, item)}
      </div>
    );
  };

  return (
    <div
      className={classNames(
        s.fields,
        justify && s.justify,
        fullWidthValues && s.fullWidthValues,
        className,
      )}
    >
      {items.filter(item => item.show ?? true).map(item => displayItem(item))}
    </div>
  );
};

export default KeyValueList;
